import i18next from 'i18next';
import {
    AsDisabled,
    LimitationProps,
    AsRestricted,
    AsAllowed,
    Restricted,
    BlockLike,
    Allowed
} from '~/components/Builder/Common/CreationLimitations/creationLimitations.types';
import { calculatePageOrder } from '~/utils/page.utils';
import { TFunction } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';

export const forbidServiceBlockInFlowWithPaidInvoice = ({
    targetFlow
}: LimitationProps<'targetFlow'>) => {
    const t = i18next.getFixedT(
        null,
        'builder',
        'creation_limitation_dialog.service_creation_limitation'
    );

    if (targetFlow.hasPaidPayments) {
        return AsDisabled(t('client_made_a_payment'));
    }

    return AsAllowed;
};

export interface ServiceSelectionAddPageAndBlockRestrictionResult
    extends Restricted {
    solutionData: {
        newPageOrder: number | undefined;
        blockType: 'service_selection_block';
        compositionType: undefined;
        newPageName: 'Services';
        direction: 'top';
        droppedItemId: string;
        dropTargetId: string;
    };
}

export interface ForbidServiceBlockAfterInvoiceBlockRestriction
    extends ServiceSelectionAddPageAndBlockRestrictionResult {
    issueType: 'forbidServiceBlockAfterInvoiceBlock';
}

const forbidServiceBlockAfterInvoiceBlock = ({
    targetFlow,
    targetPage
}: LimitationProps<'targetFlow' | 'targetPage'>) => {
    const t = i18next.getFixedT(
        null,
        'builder',
        'creation_limitation_dialog.service_creation_limitation'
    );

    const pageOrder = targetPage?.order ?? Infinity; // if no page is provided - that means it's a new page.

    if (
        targetFlow.pageWithInvoice &&
        targetFlow.pageWithInvoice.order <= pageOrder
    ) {
        const newPageOrder = calculatePageOrder({
            pages: targetFlow.pages,
            relativeToPage: targetFlow.pageWithInvoice,
            shouldAddBeforePage: true
        });

        return AsRestricted<ForbidServiceBlockAfterInvoiceBlockRestriction>({
            issueType: 'forbidServiceBlockAfterInvoiceBlock',
            reason: {
                short: t('after_invoice.title'),
                long: t('after_invoice.description'),
                cta: t('after_invoice.approve_cta'),
                rejectCta: t('reject_cta')
            },
            solutionData: {
                newPageOrder,
                blockType: 'service_selection_block',
                compositionType: undefined,
                newPageName: 'Services',
                direction: 'top',
                droppedItemId: targetPage?.page_id,
                dropTargetId: targetFlow.pageWithInvoice.page_id
            }
        });
    }

    return AsAllowed;
};

export interface ForbidServiceBlockAfterContractBlockWithServiceRecapsRestriction
    extends ServiceSelectionAddPageAndBlockRestrictionResult {
    issueType: 'forbidServiceBlockAfterContractBlockWithServiceRecaps';
}

const forbidServiceBlockAfterContractBlockWithServiceRecaps = ({
    targetFlow,
    targetPage
}: LimitationProps<'targetFlow' | 'targetPage'>) => {
    const t = i18next.getFixedT(
        null,
        'builder',
        'creation_limitation_dialog.service_creation_limitation'
    );

    const pageOrder = targetPage?.order ?? Infinity; // if no page is provided - that means it's a new page.

    if (
        targetFlow.firstContractWithServicesRecapsPage &&
        targetFlow.firstContractWithServicesRecapsPage.order < pageOrder
    ) {
        const newPageOrder = calculatePageOrder({
            pages: targetFlow.pages,
            relativeToPage: targetFlow.firstContractWithServicesRecapsPage,
            shouldAddBeforePage: true
        });

        return AsRestricted<ForbidServiceBlockAfterContractBlockWithServiceRecapsRestriction>(
            {
                issueType:
                    'forbidServiceBlockAfterContractBlockWithServiceRecaps',
                reason: {
                    short: t('after_contract_with_recaps.title'),
                    long: t('after_contract_with_recaps.description'),
                    cta: t('after_contract_with_recaps.approve_cta'),
                    rejectCta: t('reject_cta')
                },
                solutionData: {
                    newPageOrder: newPageOrder,
                    blockType: 'service_selection_block',
                    compositionType: undefined,
                    newPageName: 'Services',
                    dropTargetId:
                        targetFlow.firstContractWithServicesRecapsPage.page_id,
                    direction: 'top',
                    droppedItemId: targetPage?.page_id
                }
            }
        );
    }

    return AsAllowed;
};

export const forbidServiceBlockAfterPagesWithDependentBlocks = (
    props: LimitationProps<'targetFlow' | 'targetPage' | 'sourceBlock'>
) => {
    // Latest version of TS does this narrowing after filter automatically so this will not be needed.
    type RestrictionsOnly = Exclude<
        (typeof restrictedLimitationsSortedByNewPageOrder)[number],
        Allowed
    >;
    const restrictedLimitationsSortedByNewPageOrder = [
        forbidServiceBlockAfterInvoiceBlock(props),
        forbidServiceBlockAfterContractBlockWithServiceRecaps(props),
        forbidSessionSelectionBlockAfterSchedulerBlock(props)
    ]
        .filter(x => x.status === 'restricted')
        .sort(
            (a: RestrictionsOnly, b: RestrictionsOnly) =>
                a.solutionData.newPageOrder - b.solutionData.newPageOrder
        );

    return restrictedLimitationsSortedByNewPageOrder[0] || AsAllowed;
};

export const forbidTwoSessionSelectionBlocks = ({
    targetFlow,
    targetBlock,
    sourceFlowTemplate,
    sourceBlock
}: LimitationProps<
    'targetFlow' | 'targetPage',
    'sourceForMessage' | 'sourceBlock' | 'targetBlock' | 'sourceFlowTemplate'
>) => {
    const t: TFunction<typeof NAMESPACES.BUILDER> = i18next.getFixedT(
        null,
        NAMESPACES.BUILDER
    );

    if (
        !!targetFlow.sessionSelectionBlock &&
        !isSessionSelectionBlockLike(targetBlock) &&
        (isSessionSelectionBlockLike(sourceBlock) ||
            sourceFlowTemplate?.action_block_data?.service_selection_type ===
                'session')
    ) {
        return AsDisabled(
            t(
                'creation_limitation_dialog.service_creation_limitation.session_selection_already_exists'
            )
        );
    }

    return AsAllowed;
};

export interface ForbidSessionSelectionBlockAfterSchedulerBlock
    extends Restricted {
    issueType: 'forbidSessionSelectionBlockAfterSchedulerBlock';
    solutionData: {
        direction: 'top';
        droppedItemId: string;
        dropTargetId: string;
        newPageOrder: number;
        blockType: 'service_selection_block';
        compositionType: undefined;
        newPageName: string;
    };
}

const forbidSessionSelectionBlockAfterSchedulerBlock = ({
    targetFlow,
    targetPage,
    sourceFlowTemplate,
    sourceBlock
}: LimitationProps<
    'targetFlow' | 'targetPage' | 'sourceBlock',
    'sourceForMessage' | 'sourceFlowTemplate'
>) => {
    if (
        targetFlow.hasScheduleBlock &&
        (!targetPage ||
            targetPage.order > targetFlow.pageWithScheduler.order) &&
        (isSessionSelectionBlockLike(sourceBlock) ||
            sourceFlowTemplate?.action_block_data?.service_selection_type ===
                'session')
    ) {
        const keyPrefix =
            'creation_limitation_dialog.service_creation_limitation.session_selection_after_scheduler';
        const t = i18next.getFixedT(null, NAMESPACES.BUILDER) as TFunction<
            typeof NAMESPACES.BUILDER
        >;

        const newPageOrder = calculatePageOrder({
            pages: targetFlow.pages,
            relativeToPage: targetFlow.pageWithScheduler,
            shouldAddBeforePage: true
        });

        return AsRestricted<ForbidSessionSelectionBlockAfterSchedulerBlock>({
            issueType: 'forbidSessionSelectionBlockAfterSchedulerBlock',
            reason: {
                short: t(`${keyPrefix}.title`),
                long: t(`${keyPrefix}.description`),
                cta: t(`${keyPrefix}.approve_cta`),
                rejectCta: null
            },
            solutionData: {
                direction: 'top',
                droppedItemId: targetPage?.page_id,
                newPageOrder,
                dropTargetId: targetFlow.pageWithScheduler?.page_id,
                blockType: 'service_selection_block',
                compositionType: undefined,
                newPageName: t('default_page_titles.session_selection')
            }
        });
    }

    return AsAllowed;
};

function isSessionSelectionBlockLike(block?: BlockLike) {
    return (
        block &&
        block.type === 'service_selection_block' &&
        block.service_selection_type === 'session'
    );
}
